import React, { useMemo } from 'react';
import { isFinite, isNumber, round } from 'lodash';
import { Box, Typography, type TypographyVariant, useTheme } from '@mui/material';
import { mapDegreesCardinal, conditionClassModifier } from '@surfline/web-common';
import classNames from 'classnames/bind';
import type { Height } from 'types/units';
import { SwellArrowIcon } from '@wavetrak/theme';
import { useMaxWidthLandscapeMobile } from '../../ForecastTableV2/hooks/useTableData';
import TableCellTooltip from '../TableCellTooltip';
import styles from './SwellCell.module.scss';

const cx = classNames.bind(styles);
const getConditionsClassName = (className: string, conditions: string, loading?: boolean) =>
  cx({
    [className]: true,
    [`${className}--${conditionClassModifier(conditions)}`]: !loading,
  });

type Props = {
  direction?: number | null;
  isPrimary?: boolean;
  loading?: boolean;
  noSwell: boolean;
  rating?: string | undefined;
  slideIndex?: number;
  swellHeight?: number | null;
  swellHeightUnits: Height | string | undefined;
  swellInterval?: number | null;
  swellIntervalUnits: string | undefined;
  unitsVariant?: TypographyVariant;
  unitsClassName?: string;
  valuesVariant?: TypographyVariant;
  valuesClassName?: string;
};

const SwellCell: React.FC<Props> = ({
  direction,
  isPrimary = false,
  loading,
  noSwell,
  rating,
  slideIndex,
  swellHeight,
  swellHeightUnits,
  swellInterval,
  swellIntervalUnits,
  unitsClassName,
  unitsVariant,
  valuesClassName,
  valuesVariant,
}) => {
  const theme = useTheme();
  const error = !isFinite(swellInterval) || !isFinite(swellHeight) || !isNumber(direction);
  const loadingOrError = loading || error;
  const isLandscapeMobile = useMaxWidthLandscapeMobile();
  const empty = swellInterval === 0 && swellHeight === 0 && direction === 0;

  const heightText = useMemo(
    () => (loadingOrError ? '-.-' : round(swellHeight || 0, 1)),
    [loadingOrError, swellHeight],
  );
  const intervalText = useMemo(
    () => (loadingOrError ? '--' : round(swellInterval || 0, 0)),
    [loadingOrError, swellInterval],
  );

  const textColor = useMemo(() => {
    const interval = swellInterval || 0;

    if (loadingOrError) return 'text.light';
    if (interval <= 4) return 'text.secondary';

    return 'text.primary';
  }, [loadingOrError, swellInterval]);

  const arrowColor = useMemo(() => {
    const interval = swellInterval || 0;
    if (loadingOrError) return theme.palette.text.light;
    if (interval <= 4) return theme.palette.text.secondary;

    return theme.palette.text.brandSecondary;
  }, [
    loadingOrError,
    swellInterval,
    theme.palette.text.brandSecondary,
    theme.palette.text.light,
    theme.palette.text.secondary,
  ]);

  const arrowRotation = useMemo(
    () => (loadingOrError ? 180 : direction - 180),
    [direction, loadingOrError],
  );

  const tooltipTitle = useMemo(
    () => (
      <Typography color="white" data-testid="forecast-table-swell-tooltip" variant="callout2">
        {mapDegreesCardinal(direction)}, {round(direction || 0, 0)}º
      </Typography>
    ),
    [direction],
  );

  return (
    <div className={styles.forecastTableSwellWrapper} data-testid="forecast-table-swell-cell">
      <div className={cx({ [styles.forecastTableSwell]: true, [styles.isPrimary]: isPrimary })}>
        {noSwell || empty ? (
          <div className={styles.noSwell}>
            <Typography
              color="text.secondary"
              component="p"
              data-testid="forecast-table-swell-no-swell"
              variant="caption1"
            >
              {noSwell ? 'No incoming swell' : null}
            </Typography>
          </div>
        ) : (
          <div
            className={styles.forecastTableSwellContent}
            data-testid="forecast-table-swell-content"
          >
            <Typography className={styles.swellHeightWrapper} component="span">
              <Typography
                className={valuesClassName || styles.measurement}
                color={textColor}
                component="h3"
                data-testid="forecast-table-swell-height"
                variant={valuesVariant || ('subHeadline' as TypographyVariant)}
              >
                {heightText}
              </Typography>
              <Typography
                className={unitsClassName || styles.units}
                color={textColor}
                component="p"
                data-testid="forecast-table-swell-height-units"
                variant={unitsVariant || 'caption1'}
              >
                {swellHeightUnits?.toLocaleLowerCase()}
              </Typography>
            </Typography>
            <Typography className={styles.swellPeriodWrapper} component="span">
              <Typography
                className={valuesClassName || styles.measurement}
                color={textColor}
                component="h3"
                data-testid="forecast-table-swell-interval"
                variant={valuesVariant || 'subHeadline'}
              >
                {intervalText}
              </Typography>
              <Typography
                className={unitsClassName || styles.units}
                color={textColor}
                component="p"
                data-testid="forecast-table-swell-interval-units"
                variant={unitsVariant || 'caption1'}
              >
                {swellIntervalUnits?.toLocaleLowerCase()}
              </Typography>
            </Typography>
            {error && !loading ? null : (
              <TableCellTooltip title={tooltipTitle} loading={loading || noSwell}>
                <Box className={styles.arrow} data-testid="forecast-table-swell-arrow-wrapper">
                  <SwellArrowIcon
                    size="extra-large"
                    rotation={arrowRotation}
                    fill={arrowColor}
                    data-testid="forecast-table-swell-arrow"
                  />
                </Box>
              </TableCellTooltip>
            )}
          </div>
        )}
        {rating && isLandscapeMobile && !loadingOrError && slideIndex === 0 && (
          <div
            className={getConditionsClassName('text', rating || '', loading)}
            data-testid="mobile-forecast-table-surf-condition"
          >
            <Typography variant="overline3" className={styles.ratingText}>
              {rating.replace(/_/g, ' ')}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};

export default SwellCell;
